import { vec3 } from 'gl-matrix';
export function createPlaneBasis(normal, orientation) {
    const n = vec3.normalize(vec3.create(), normal);
    const along = vec3.dot(orientation, n);
    const u = vec3.sub(vec3.create(), orientation, vec3.scale(vec3.create(), n, along));
    const uLength = vec3.length(u);
    if (!(uLength > 1e-12)) {
        throw new Error('Shape orientation vector is parallel to the plane normal, so it defines no in-plane direction');
    }
    vec3.scale(u, u, 1 / uLength);
    const v = vec3.cross(vec3.create(), n, u);
    return { u: u, v, n };
}
export function getAxisSteps(volume) {
    const { direction, spacing } = volume;
    return [0, 1, 2].map((axis) => {
        const unit = direction.slice(axis * 3, axis * 3 + 3);
        return [
            unit[0] * spacing[axis],
            unit[1] * spacing[axis],
            unit[2] * spacing[axis],
        ];
    });
}
export function createColumnLineResolver(volume, normal) {
    const axisSteps = getAxisSteps(volume);
    const { origin } = volume;
    const line = {
        base: [0, 0, 0],
        step: [0, 0, 0],
        projectedBase: [0, 0, 0],
        projectedStep: [0, 0, 0],
    };
    let preparedSlab = null;
    return function resolve(slab, outerIndex, rowIndex) {
        const { g, c0, outerAxis, rowAxis, columnAxis } = slab;
        if (preparedSlab !== slab) {
            const columnStep = axisSteps[columnAxis];
            const gColumn = g[columnAxis];
            for (let axis = 0; axis < 3; axis++) {
                line.step[axis] = columnStep[axis];
                line.projectedStep[axis] = columnStep[axis] - gColumn * normal[axis];
            }
            preparedSlab = slab;
        }
        const outerStep = axisSteps[outerAxis];
        const rowStep = axisSteps[rowAxis];
        const baseDepth = outerIndex * g[outerAxis] + rowIndex * g[rowAxis] + c0;
        for (let axis = 0; axis < 3; axis++) {
            const base = origin[axis] + outerIndex * outerStep[axis] + rowIndex * rowStep[axis];
            line.base[axis] = base;
            line.projectedBase[axis] = base - baseDepth * normal[axis];
        }
        return line;
    };
}
export const SHAPE_BOUNDARY_EPSILON = 1e-5;
export const UNBOUNDED = [-Infinity, Infinity];
export function intersectRanges(a, b) {
    if (!a || !b) {
        return null;
    }
    const low = Math.max(a[0], b[0]);
    const high = Math.min(a[1], b[1]);
    return low > high ? null : [low, high];
}
export function solveAbsLinearLeq(a, b, halfExtent) {
    if (b === 0) {
        return Math.abs(a) <= halfExtent ? UNBOUNDED : null;
    }
    const first = (-halfExtent - a) / b + 0;
    const second = (halfExtent - a) / b + 0;
    return first <= second ? [first, second] : [second, first];
}
export function solveQuadraticLeqZero(A, B, C) {
    if (A === 0) {
        if (B === 0) {
            return C <= 0 ? UNBOUNDED : null;
        }
        const root = -C / B + 0;
        return B > 0 ? [-Infinity, root] : [root, Infinity];
    }
    const discriminant = B * B - 4 * A * C;
    if (discriminant < 0) {
        return null;
    }
    const rootOfDiscriminant = Math.sqrt(discriminant);
    const first = (-B - rootOfDiscriminant) / (2 * A) + 0;
    const second = (-B + rootOfDiscriminant) / (2 * A) + 0;
    return first <= second ? [first, second] : [second, first];
}
export function toIntegerRun(range) {
    if (!range) {
        return null;
    }
    const low = Math.ceil(range[0]);
    const high = Math.floor(range[1]);
    return low > high ? null : [low, high];
}
